import { Link } from "react-router-dom";
import Logo from "@/components/Logo";

export default function DataDeletion() {
  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#1C1917]" data-testid="data-deletion-page">
      <header className="border-b border-[#E8E2D9] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><Logo variant="full" size={36} /></Link>
          <nav className="text-sm text-[#57534E] hidden md:flex gap-6">
            <Link to="/privacy" className="hover:text-[#E8712C]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#E8712C]">Terms</Link>
            <a href="mailto:support@clientdeskva.com" className="hover:text-[#E8712C]">Support</a>
          </nav>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl font-bold tracking-tight mb-2">Data Deletion</h1>
        <p className="text-sm text-[#57534E] mb-10">How to erase your ClientDesk VA account and data</p>

        <section className="space-y-6 text-[15px] leading-relaxed">
          <h2 className="font-display text-2xl font-semibold">Delete your account from inside the app</h2>
          <p>The fastest way is from within ClientDesk itself:</p>
          <ol className="list-decimal pl-6 space-y-1">
            <li>Sign in at <Link to="/login" className="text-[#E8712C]">/login</Link>.</li>
            <li>Open <b>Settings → Account</b>.</li>
            <li>Click <b>Delete my account</b> and follow the confirmation prompts.</li>
          </ol>
          <p>Your Supabase account is scheduled for permanent deletion within 30 days. Connected Google and Microsoft calendar tokens are revoked immediately. Active Stripe subscriptions are cancelled. Personal fields (name, email, phone) on records you created are scrubbed and replaced with "Deleted user".</p>

          <div className="bg-[#FDE9D8] border border-[#F0C69A] rounded-2xl p-4 my-6">
            <h3 className="font-semibold text-[#B85C1F] mb-1">What is retained and why</h3>
            <p className="text-sm text-[#7A4520]">Financial records (invoices, receipts, subscription history) are retained for <b>seven years</b> to satisfy tax and regulatory obligations. They are stripped of personally identifiable information linked to you where legally permissible. Audit-log entries needed for fraud detection may be retained for <b>two years</b>.</p>
          </div>

          <h2 className="font-display text-2xl font-semibold mt-8">Organization deletion</h2>
          <p>If you are the last <b>Org Admin</b> and your organization has active members, we will ask you to <b>transfer ownership</b> to another admin, remove the other members, or accept full-organization deletion. Full-organization deletion removes all clients, appointments, tasks, documents, forms, contracts, and non-financial records for that org.</p>

          <h2 className="font-display text-2xl font-semibold mt-8">Portal (client) users</h2>
          <p>Portal users are records inside an organization's ClientDesk workspace, not standalone accounts. If you were invited by an organization and want your record deleted:</p>
          <ol className="list-decimal pl-6 space-y-1">
            <li>Contact the organization that invited you directly (they are the data controller of your record).</li>
            <li>If they do not respond within 30 days, email <a href="mailto:privacy@clientdeskva.com" className="text-[#E8712C]">privacy@clientdeskva.com</a> and we will escalate.</li>
          </ol>

          <h2 className="font-display text-2xl font-semibold mt-8">Email us to delete</h2>
          <p>Prefer email? Send a deletion request from the email address on your account to <a href="mailto:privacy@clientdeskva.com" className="text-[#E8712C]">privacy@clientdeskva.com</a> with subject <b>"Delete my account"</b>. We will confirm your identity, delete within 30 days, and email you when it is complete.</p>

          <h2 className="font-display text-2xl font-semibold mt-8">Timeline</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><b>Immediate</b> — you are signed out and cannot sign back in.</li>
            <li><b>Immediate</b> — Google/Outlook OAuth tokens revoked, calendar subscriptions cancelled.</li>
            <li><b>Immediate</b> — Stripe subscriptions cancelled.</li>
            <li><b>Within 24 hours</b> — personal fields on your records scrubbed.</li>
            <li><b>Within 30 days</b> — Supabase auth account permanently deleted.</li>
            <li><b>7 years</b> — financial records retained (stripped of PII where lawful).</li>
          </ul>

          <hr className="my-10" />
          <p className="text-xs text-[#57534E]">Questions? <a href="mailto:privacy@clientdeskva.com" className="text-[#E8712C]">privacy@clientdeskva.com</a></p>
        </section>
      </main>
    </div>
  );
}
